import { sortBy, sumBy } from 'lodash'

import {
  dynamicClone,
  findBlocks,
} from '../templates/templateEditor/editorHelper'
import { generateId } from '../templates/templateRenderer/utils/generateTemplateIds'
import {
  TemplateData,
  SWMSGeneralInfoDisplayBlock,
  SWMSHRCWDisplayBlock,
  SWMSHazardsDisplayBlock,
  SWMSPPEDisplayBlock,
  SWMSPlantDisplayBlock,
  Section,
  FileDisplayBlock,
} from '../templates/types/blueprint'

type SWMSBlocks = {
  swmsGeneralInfo: SWMSGeneralInfoDisplayBlock
  swmsHrcw: SWMSHRCWDisplayBlock
  swmsHazards: SWMSHazardsDisplayBlock
  swmsPpe: SWMSPPEDisplayBlock
  swmsPlant: SWMSPlantDisplayBlock
  file: FileDisplayBlock
}

export type SWMSBlock = SWMSBlocks[keyof SWMSBlocks]
export type SWMSBlockTypes = keyof typeof SWMS_BLOCKS

/**
 * Global definition of the SWMS blocks, ORDER IS IMPORTANT as it
 * influences the order of the swms builder
 */
export const SWMS_BLOCKS = {
  swmsGeneralInfo: { title: 'General Information', description: '' },
  swmsHrcw: {
    title: 'HRCW',
    description: 'High risk construction work categories taking place on site',
  },
  swmsPlant: {
    title: 'Plant & Equipment',
    description: `Plant and work equipment required to complete the activities on site`,
  },
  swmsHazards: {
    title: 'Work Activities & Hazards',
    description: `Outlining the safety measures and controls for each potential hazard related to each activity to be carried out on site`,
  },
  swmsPpe: {
    title: 'Personal Protective Equipment',
    description: 'PPE to be worn by all workers as required',
  },
  file: {
    title: 'Attachments (optional)',
    description: 'Supporting or required documents',
  },
}

const replaceBlock = (
  swmsTemplate: TemplateData,
  mergedBlock: SWMSBlock
): TemplateData => {
  let replaced = false

  return {
    ...swmsTemplate,
    sections: swmsTemplate?.sections?.map((section) => ({
      ...section,
      blockGroups: section?.blockGroups?.map((blockGroup) => ({
        ...blockGroup,
        blocks: blockGroup?.blocks
          ?.map((block) => {
            if (block.type === mergedBlock.type) {
              if (!replaced) {
                replaced = true
                return mergedBlock
              } else {
                return null // remove duplicate block types
              }
            }
            return { ...block }
          })
          .filter((b) => !!b),
      })),
    })),
  }
}

export const findByType = <T extends SWMSBlockTypes>(
  type: T,
  template: TemplateData
): SWMSBlocks[T][] => {
  return findBlocks(template, (b) => b.type === type) as SWMSBlocks[T][]
}

export const upsertSwmsBlock = (
  block: SWMSBlock,
  swmsTemplate: TemplateData
): TemplateData => {
  let template: TemplateData
  const foundBlocks = findByType(block.type, swmsTemplate)

  if (foundBlocks.length) {
    template = replaceBlock(swmsTemplate, block)
  } else {
    const clonedTemplate = dynamicClone('TEMPLATE', swmsTemplate)
    const { type, kind } = block

    template = {
      ...clonedTemplate,
      sections: [
        ...(clonedTemplate.sections || []),
        {
          id: generateId('SECTION'),
          name: SWMS_BLOCKS[block.type]?.title,
          description: SWMS_BLOCKS[block.type]?.description,
          blockGroups: [
            {
              id: generateId('BLOCK_GROUP'),
              blocks: [
                {
                  id: generateId('BLOCK', { type, kind }),
                  ...block,
                },
              ],
            },
          ],
        },
      ],
    }
  }

  const sortFuncs = Object.keys(SWMS_BLOCKS)
    .reverse()
    .map(
      (blockType: string) => (section: Section) =>
        section.blockGroups[0].blocks.some((b) => b.type === blockType)
    )

  const sortedTemplate: TemplateData = {
    ...template,
    sections: sortBy(template.sections, sortFuncs),
  }

  return sortedTemplate
}

type HazardRow = {
  id: string
  workActivity: string
  hazard: string
  responsible: string[]
  control: string
  activityRowSpan: number
  hazardRowSpan: number
}

const getRowSpan = (first: boolean, rows: number) => {
  if (rows <= 1) return 1
  if (first) return rows
  return 0
}

export const transformHazardsForTable = (
  activities: SWMSHazardsDisplayBlock['value']
) => {
  const rows: HazardRow[] = []

  if (!activities?.length) return rows

  for (const activity of activities) {
    const activityRowSpan = sumBy(activity.hazards, (h) => h.controls.length)
    let firstActivity = true

    for (const hazard of activity.hazards) {
      let firstHazard = true

      for (const control of hazard.controls) {
        rows.push({
          id: `${activity.description}-${hazard.description}-${control}`,
          workActivity: activity.description,
          hazard: hazard.description,
          control,
          responsible: hazard.responsible,
          activityRowSpan: getRowSpan(firstActivity, activityRowSpan),
          hazardRowSpan: getRowSpan(firstHazard, hazard.controls.length),
        })

        firstActivity = false
        firstHazard = false
      }
    }
  }

  return rows
}

export const stripGeneralInfo = (template: TemplateData) => {
  return {
    ...template,
    sections: template.sections?.filter((section) => {
      return section.blockGroups?.every((blockGroup) => {
        return blockGroup.blocks?.every((b) => b.type !== 'swmsGeneralInfo')
      })
    }),
  }
}
